import { useState } from 'react';
import { Button, Group, NumberInput, Paper, Select, Stack, TextInput, Textarea } from '@mantine/core';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { ActiveAiModelsSubscription, UpdatePersonaMutation } from './graphql';
import type { PersonaDetail } from './graphql';

export function PersonaSettingsPanel({ persona }: { persona: PersonaDetail }) {
  const [{ data: modelsData }] = useSubscription({ query: ActiveAiModelsSubscription });
  const [, runUpdatePersona] = useMutation(UpdatePersonaMutation);

  const [name, setName] = useState(persona.name);
  const [description, setDescription] = useState(persona.description);
  const [greeting, setGreeting] = useState(persona.greeting);
  const [modelId, setModelId] = useState(persona.modelId ?? null);
  const [maxTurns, setMaxTurns] = useState(persona.maxTurns);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    const result = await runUpdatePersona({
      personaId: persona.id,
      set: { name: name.trim(), description, greeting, modelId, maxTurns },
    });

    setIsSaving(false);
    setError(result.error ? getErrorMessage(result.error, 'Failed to save settings') : null);
  };

  return (
    <Paper p="lg" withBorder radius="lg">
      <Stack gap="md">
        <ErrorAlert message={error} />
        <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <TextInput
          label="Greeting"
          description="The initial message spoken to the caller"
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
        />
        <Select
          label="AI Model"
          description="Override the default AI model for this persona"
          placeholder="Use system default"
          value={modelId}
          onChange={setModelId}
          data={(modelsData?.aiModels ?? []).map((m) => ({
            value: m.id,
            label: `${m.name}${m.isDefault ? ' (Default)' : ''}`,
          }))}
          clearable
        />
        <NumberInput
          label="Default Max Turns"
          description="Default maximum conversation turns (can be overridden per widget)"
          value={maxTurns}
          onChange={(value) => setMaxTurns(typeof value === 'number' ? value : 10)}
          min={1}
          max={50}
        />
        <Group justify="flex-end">
          <Button onClick={handleSave} loading={isSaving} disabled={!name.trim()}>
            Save Settings
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
